import Icon from '@components/icons/Icon';
import { theme } from 'aimo-ui';
import MenuItem from 'components/common/MenuItem';
import QuickActionDirection from 'components/common/QuickActionDirection';
import { add, isWithinInterval } from 'date-fns';
import { Position } from 'geojson';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LocationType,
  Maybe,
  PaymentType,
  PoolingInfo,
} from 'types/generatedSchemaTypes';
import { makePoint } from 'utils/mapUtils';
import { locationIcon } from 'utils/parkUtils';
import {
  getPaymentMethodLogo,
  getWalletPaymentMethodIcon,
  isWalletCard,
} from 'utils/paymentUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { getDurationFromDates } from 'utils/timeUtils';
import usePaymentDetails from 'hooks/usePaymentDetails.tsx';
import useReadReceiptByTransactionId from 'hooks/useReadReceiptByTransactionId.tsx';
import AccountContext from 'context/AccountContext.tsx';

type ParkingDetailsSummaryProps = {
  name?: Maybe<string>;
  locationType?: Maybe<LocationType>;
  endTime: Maybe<Date>;
  startTime: Maybe<Date>;
  markerType: string;
  doorCode?: Maybe<string>;
  coordinates?: Maybe<Position>;
  poolingInfo?: Maybe<PoolingInfo>;
  failedPayment?: boolean;
  cardId?: Maybe<string>;
  transactionId?: Maybe<string>;
  corporateAccountUid?: Maybe<string>;
  totalAmount?: Maybe<number>;
  paymentType?: Maybe<PaymentType>;
};
const ParkingDetailsSummary = ({
  endTime,
  startTime,
  name,
  markerType,
  doorCode,
  coordinates,
  poolingInfo,
  failedPayment,
  cardId,
  transactionId,
  corporateAccountUid,
  totalAmount,
  paymentType,
}: ParkingDetailsSummaryProps) => {
  const { t } = useTranslation();
  const {
    customer: { paymentCards },
  } = useContext(AccountContext);
  const usedCard = paymentCards?.find((card) => cardId === card?.cardId);
  const { type: cardType } = usedCard || {};

  const { data } = useReadReceiptByTransactionId(transactionId ?? '');
  const otherPaymentProvider =
    data?.readReceiptByTransactionId?.paymentProvider === 'SWISH'
      ? 'SWISH'
      : null;

  const doesNotRequirePayment = paymentType === 'NONE';

  const paymentDetails = usePaymentDetails({
    transactionId,
    cardId,
    corporateAccountUid,
    totalAmount,
    otherPaymentProvider,
    paymentType,
  });

  const duration =
    startTime && endTime ? getDurationFromDates(startTime, endTime) : '';

  const showDoorCode =
    doorCode !== null &&
    doorCode !== '' &&
    endTime &&
    isWithinInterval(new Date(), {
      start: endTime,
      end: add(endTime, { minutes: 15 }),
    });

  return (
    <>
      {showDoorCode && (
        <MenuItem
          testID="garage-entry-code"
          iconName={'Info-Outlined'}
          menuLabel={t('parking.garageEntryCode')}
          smallLabel={doorCode}
        />
      )}
      <MenuItem
        testID="zone-name"
        iconName={locationIcon[markerType]}
        menuLabel={name || ''}
        rightComponent={
          coordinates ? (
            <QuickActionDirection
              name={name}
              showIconLabel={false}
              markerCoordinate={makePoint(coordinates)}
            />
          ) : undefined
        }
      />
      <MenuItem
        testID="duration"
        iconName="Time-Outlined"
        menuLabel={t('parking.duration') + ' ' + duration}
        smallLabel={duration}
        noBorder={failedPayment}
      />
      {poolingInfo && (
        <MenuItem
          {...testIdentifiers('pooling-row-details')}
          iconName="Services-Outlined"
          menuLabel={t('parking.receipt.pooling')}
          noBorder={failedPayment}
          rightComponent={
            <Icon
              {...testIdentifiers('pooling-row-icon')}
              name={poolingInfo.pooled ? 'check' : 'close'}
              color={theme.colors.gray300}
            />
          }
        />
      )}
      {
        <MenuItem
          testID="payment-details"
          noBorder
          iconName={
            // A stored wallet card shows its wallet brand mark, never the
            // underlying scheme logo.
            isWalletCard(usedCard)
              ? getWalletPaymentMethodIcon(usedCard?.paymentMethod)
              : getPaymentMethodLogo(paymentType ?? null, cardType, false)
                  ?.logoName
          }
          menuLabel={
            paymentDetails?.paymentType === 'CORPORATE_ACCOUNT' ||
            paymentDetails?.paymentType === 'EXTERNAL'
              ? t('services.paymentMethod')
              : paymentDetails?.paymentMethodTitle || ''
          }
          smallLabel={
            doesNotRequirePayment
              ? t('paymentMethods.paymentNotRequired')
              : paymentDetails?.paymentMethodName
          }
          subtitle={
            paymentDetails?.paymentType === 'CORPORATE_ACCOUNT' ||
            paymentDetails?.paymentType === 'EXTERNAL'
              ? paymentDetails?.paymentMethodTitle
              : ''
          }
        />
      }
    </>
  );
};

export default ParkingDetailsSummary;
